import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import client from '../lib/api';
import { getRefPayload, clearRef } from '../lib/referral';
import { markJustRegistered, setPromotionViewer } from '../lib/cmsPopups';
import { setBannerViewer } from '../lib/sliderPlacement';
import realtime, { newerWallet } from '../lib/realtime';
import { tokenExpiry } from '../lib/sessionToken';
import {
  AUTH_FAILURE_EVENT, TOKEN_KEY, readToken, requestToken, sessionFailure, tokenSubject, writeToken,
} from '../lib/sessionAuth';
import { toast } from '../hooks/use-toast';

const AuthContext = createContext(null);
const GAME_SESSION_KEY = 'pusubet_game_session_id';
const ACCOUNT_SYNC_INTERVAL_MS = 15000;
// Gerçek zamanlı kanal canlıyken /auth/me yalnız bu aralıkla (güvenlik ağı)
// çağrılır; bakiye ve hesap değişiklikleri kanaldan gelir (lib/realtime.js).
const ACCOUNT_SYNC_LIVE_MS = 120000;
const HEARTBEAT_INTERVAL_MS = 30000;
// Kayan oturum: jetonun bitimine bu kadar kala sessizce yenilenir
// (``POST /auth/refresh``). Jeton 12 saat geçerlidir; yenilenmezse uzun
// oynayan oyuncu oyunun ortasında çıkış yapmış olur (BETRUN, 23.10.2026).
export const SESSION_REFRESH_BEFORE_MS = 2 * 60 * 60 * 1000;
const SESSION_CHECK_INTERVAL_MS = 60000;
// Sunucunun izin verdiği en uzun bekleme (balance_watch.MAX_WAIT_SECONDS) ve
// nginx'in 60 sn'lik proxy_read_timeout'unun altında kalır.
export const BALANCE_WAIT_SECONDS = 25;
const BALANCE_WAIT_RETRY_MS = 5000;
// Kanal kurulurken yedek bekleme isteği en çok bu kadar ertelenir.
export const REALTIME_GRACE_MS = 4000;
// Açılışta /auth/me geçici hata alırsa (ağ, 5xx, yayın anı) jeton korunur ve
// bu aralıklarla yeniden denenir; ağ geri gelince ya da sekme öne gelince hemen.
export const SESSION_RETRY_DELAYS_MS = [2000, 5000, 10000, 20000, 30000];

const SESSION_ENDED = {
  title: 'Oturumunuz sona erdi',
  description: 'Güvenliğiniz için lütfen tekrar giriş yapın.',
};
const SESSION_CLOSED_ELSEWHERE = {
  title: 'Oturumunuz kapandı',
  description: 'Başka bir sekmede çıkış yapıldı ya da oturum sona erdi. Lütfen tekrar giriş yapın.',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Banner kitlesi (üye/ziyaretçi) çizim sırasında okunur; render'da yazılır
  // ki aynı turdaki kabuklar güncel değeri görsün (yazım idempotenttir).
  setBannerViewer({ loggedIn: Boolean(user) });
  setPromotionViewer(user);
  const [ready, setReady] = useState(false);
  // Olay dinleyicileri (sekmeler arası, oturum hatası) güncel oturumu render
  // beklemeden görür; oturum kapatılınca hemen boşaltılır ki aynı anda gelen
  // ikinci 401 ikinci bir uyarı göstermesin.
  const userRef = React.useRef(null);
  userRef.current = user;
  // Bilinçli çıkış sürerken gelen 401 uyarı göstermez.
  const loggingOutRef = React.useRef(false);

  /** Oturumu bu sekmede kapatır (jeton, kanal, ekran).
   *
   * ``usedToken``: hatayı alan isteğin jetonu. Depodaki jeton ondan farklıysa
   * (bu arada başka sekmede yeni giriş ya da yenileme oldu) eski jetonun 401'i
   * yeni oturumu silmez; yeni jetonla hesap yeniden okunur.
   */
  const refreshRef = React.useRef(() => Promise.resolve());
  const endSession = useCallback(({ usedToken = '', notice = SESSION_ENDED, notify = true } = {}) => {
    const current = readToken();
    if (usedToken && current && current !== usedToken) {
      refreshRef.current();
      return;
    }
    const hadUser = Boolean(userRef.current);
    if (!current && !hadUser) return;
    if (current) writeToken(null);
    userRef.current = null;
    realtime.stop();
    setUser(null);
    if (notify && hadUser && !loggingOutRef.current) {
      toast(notice);
      window.dispatchEvent(new CustomEvent('bhs:session-expired'));
    }
  }, []);

  // Geçici hatada açılış tazelemesinin yeniden denenmesi.
  const retryRef = React.useRef({ timer: null, attempt: 0 });
  const clearRetry = () => {
    window.clearTimeout(retryRef.current.timer);
    retryRef.current = { timer: null, attempt: 0 };
  };
  const scheduleRetry = () => {
    const state = retryRef.current;
    if (state.timer) return;
    const delay = SESSION_RETRY_DELAYS_MS[Math.min(state.attempt, SESSION_RETRY_DELAYS_MS.length - 1)];
    state.attempt += 1;
    state.timer = window.setTimeout(() => {
      state.timer = null;
      // Arka plandaki sekme denemez; öne gelince (aşağıdaki dinleyici) dener.
      if (document.visibilityState !== 'hidden') refreshRef.current();
    }, delay);
  };

  /** Hesabı jetonla sunucudan okur.
   *
   * Jeton yalnız sunucunun **oturum** 401'inde silinir. Ağ kopması, zaman
   * aşımı, kesilen istek (499), 500/503 (yayın, bakım) ve 429 oturumu
   * kapatmaz: girişli ekran olduğu gibi kalır, açılıştaysa yeniden denenir.
   * Eskiden her hata jetonu siliyordu; telefonda banka uygulamasından dönen,
   * oyundan çıkan ya da kesinti anında sayfayı yenileyen oyuncu çıkış yapmış
   * oluyordu (BETRUN, 24-27.10.2026).
   */
  const refresh = useCallback(async (nextToken) => {
    // 2FA değişikliği eski oturumları geçersiz kılar; yeni jeton ilk isteğe yetişmelidir.
    if (typeof nextToken === 'string' && nextToken) {
      writeToken(nextToken);
      // Eski jetonun sürümü artık geçersiz: kanal yeni jetonla yeniden bağlanır.
      realtime.start(nextToken);
    }
    const t = readToken();
    if (!t) {
      clearRetry();
      // Ekran girişliyken jeton yoksa (başka sekmede silindi) oyuncuya söylenir.
      if (userRef.current) endSession({ notice: SESSION_CLOSED_ELSEWHERE });
      else setUser(null);
      setReady(true);
      return;
    }
    try {
      const { data } = await client.get('/auth/me');
      // Yanıt gelene kadar başka sekmede çıkış/giriş olduysa sonuç eskidir.
      if (readToken() !== t) { await refreshRef.current(); return; }
      clearRetry();
      setUser(data.user);
    } catch (error) {
      const kind = sessionFailure(error, { sessionEndpoint: true });
      if (!kind) {
        // Geçici hata: jeton ve (varsa) girişli ekran korunur.
        if (!userRef.current) scheduleRetry();
        return;
      }
      clearRetry();
      if (kind === 'missing' && readToken()) { await refreshRef.current(); return; }
      endSession({
        usedToken: t,
        notice: kind === 'missing' ? SESSION_CLOSED_ELSEWHERE : SESSION_ENDED,
      });
    } finally {
      setReady(true);
    }
  }, [endSession]);
  refreshRef.current = refresh;

  useEffect(() => { refresh(); }, [refresh]);

  // Açılış tazelemesi geçici hatada kaldıysa: ağ geri gelince ya da sekme öne
  // gelince beklemeden yeniden denenir (telefon kilidi, banka uygulaması).
  useEffect(() => {
    const retryNow = () => {
      if (document.visibilityState === 'hidden' || !retryRef.current.attempt) return;
      window.clearTimeout(retryRef.current.timer);
      retryRef.current.timer = null;
      refreshRef.current();
    };
    window.addEventListener('online', retryNow);
    document.addEventListener('visibilitychange', retryNow);
    return () => {
      window.removeEventListener('online', retryNow);
      document.removeEventListener('visibilitychange', retryNow);
      window.clearTimeout(retryRef.current.timer);
    };
  }, []);

  // Sekmeler arası eşitleme: jeton tüm sekmelerde ortaktır. Başka sekmede
  // çıkış yapılır ya da oturum biterse bu sekme girişli görünmeye devam
  // ediyor, istekleri jetonsuz gidiyordu (BETRUN, 24-27.10.2026: 70 cihaz).
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== null && event.key !== TOKEN_KEY) return;
      const next = readToken();
      if (!next) {
        clearRetry();
        if (userRef.current) endSession({ notice: SESSION_CLOSED_ELSEWHERE });
        return;
      }
      if (event.key !== null && event.oldValue === next) return;
      const subject = tokenSubject(next);
      if (subject && subject === userRef.current?.id) {
        // Aynı oyuncunun yenilenen jetonu: kanal yeni jetonla sürer.
        realtime.start(next);
        return;
      }
      // Başka hesapla ya da girişsiz sekmede yeni giriş: hesap yeniden okunur.
      refreshRef.current();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [endSession]);

  // Herhangi bir istekte oturum 401'i (lib/api.js): ekran girişliyken geçerli
  // oturum yoksa oyuncu bozuk ekranda bırakılmaz.
  useEffect(() => {
    const onFailure = (event) => {
      if (!userRef.current) return;
      const { kind, usedToken } = event.detail || {};
      if (kind === 'missing') {
        // Jeton depoda yoksa oturum bitmiştir; varsa istek yarışta jetonsuz gitmiştir.
        if (!readToken()) endSession({ notice: SESSION_CLOSED_ELSEWHERE });
        return;
      }
      endSession({ usedToken: usedToken || '' });
    };
    window.addEventListener(AUTH_FAILURE_EVENT, onFailure);
    return () => window.removeEventListener(AUTH_FAILURE_EVENT, onFailure);
  }, [endSession]);

  /** Hesabı sunucudan tazeler; hata oturumu kapatmaz (`refresh`'ten farkı).
   *
   * Bonus iptali gibi işlemler bakiyenin yanında çevrim alanlarını da
   * değiştirir; yalnızca bakiyeyi yazmak, hesap menüsündeki "Kalan Çevrim"
   * kutusunu arka plan yenilemesine kadar eski hâlinde bırakıyordu.
   */
  const syncUser = useCallback(async () => {
    try {
      const { data } = await client.get('/auth/me');
      setUser((current) => (
        current?.id === data.user?.id ? { ...current, ...data.user } : current
      ));
    } catch {
      // Tazeleme hatası aktif oturumu kapatmamalı.
    }
  }, []);

  // Oturum gerçekten bittiğinde (süre, çıkış, şifre değişikliği) ekran
  // oyuncuyu girişli göstermeye devam etmemeli: ilk oyun açılışı teknik bir
  // 401 ile düşüyordu. Jeton silinir, oyuncuya ne olduğu söylenir.
  // ``error``: oturum hatasını taşıyan istek hatası. Geçici hata (ağ, 5xx)
  // oturumu kapatmaz; jetonsuz giden istek depoda jeton varsa yarıştır.
  const expireSession = useCallback((error) => {
    if (!error) { endSession(); return; }
    const kind = sessionFailure(error, { sessionEndpoint: true });
    if (!kind) return;
    if (kind === 'missing') {
      if (!readToken()) endSession({ notice: SESSION_CLOSED_ELSEWHERE });
      return;
    }
    endSession({ usedToken: requestToken(error.config) });
  }, [endSession]);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    let busy = false;
    const check = async () => {
      const current = readToken();
      if (!current) {
        // Girişli ekranda jeton yok: başka sekmede silindi (storage olayı kaçtıysa).
        endSession({ notice: SESSION_CLOSED_ELSEWHERE });
        return;
      }
      const expiry = tokenExpiry(current);
      if (!expiry || busy) return;
      const left = expiry - Date.now();
      if (left <= 0) { endSession({ usedToken: current }); return; }
      if (left > SESSION_REFRESH_BEFORE_MS) return;
      busy = true;
      try {
        const { data } = await client.post('/auth/refresh');
        // Bu arada çıkış ya da başka hesapla giriş olduysa yazılmaz.
        if (data?.token && readToken() === current) {
          writeToken(data.token);
          realtime.start(data.token);
        }
      } catch (error) {
        // Yalnız oturum 401'i kapatır; ağ hatası bir sonraki turda yeniden denenir.
        expireSession(error);
      } finally {
        busy = false;
      }
    };
    check();
    const timer = window.setInterval(check, SESSION_CHECK_INTERVAL_MS);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [userId, expireSession, endSession]);

  useEffect(() => {
    if (!userId) return undefined;

    let lastSync = Date.now();
    const syncAccount = async (event) => {
      if (document.visibilityState === 'hidden') return;
      // Odak/görünürlük olayı her zaman tazeler; zamanlayıcı turu kanal
      // canlıyken yalnız ACCOUNT_SYNC_LIVE_MS'de bir.
      if (!event && realtime.isLive() && Date.now() - lastSync < ACCOUNT_SYNC_LIVE_MS) return;
      lastSync = Date.now();
      try {
        const { data } = await client.get('/auth/me');
        setUser((current) => (
          current?.id === data.user?.id ? { ...current, ...data.user } : current
        ));
      } catch (error) {
        // Ağ hatası aktif oturumu kapatmamalı; yalnız oturum 401'i kapatır.
        expireSession(error);
      }
    };

    const timer = window.setInterval(syncAccount, ACCOUNT_SYNC_INTERVAL_MS);
    window.addEventListener('focus', syncAccount);
    document.addEventListener('visibilitychange', syncAccount);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', syncAccount);
      document.removeEventListener('visibilitychange', syncAccount);
    };
  }, [userId, expireSession]);

  // Bakiye değişikliği (yönetici eklemesi, yatırım onayı) yukarıdaki 15 sn'lik
  // yoklamayı beklemeden gelir: istemci bildiği bakiyeyle bekler, sunucu
  // değişince hemen yanıtlar (bkz. backend services/balance_watch.py).
  // Yoklamadan farkı: değişiklik yoksa istek en çok BALANCE_WAIT_SECONDS
  // açık durur ve veritabanına oyuncu başına değil kopya başına sorgu düşer.
  const balanceRef = React.useRef({ balance: 0, bonus: 0, hint: '' });
  // "ANA CÜZDANA GEÇ" önerisinin damgası bakiyeyi değiştirmez; bekleme onu da
  // izler ki bonus seçiliyken reddedilen bahsin önerisi oyun ekranına anında
  // ulaşsın (bkz. backend services/bonus_wallet.record_switch_hint).
  const hintStampRef = React.useRef('');
  balanceRef.current = { balance: user?.balance ?? 0, bonus: user?.bonus_balance ?? 0, hint: hintStampRef.current };
  // Yönetim paneli sayfaları bakiye göstermez; orada açık bekleme bağlantısı
  // tutulmaz. Karar hesabın türüne değil sayfaya göredir: yönetici hesabıyla
  // oyuncu sitesinde oynayan biri de bakiyeyi görür. Hesap türüne bağlıyken
  // (`is_admin`) bu oturumlar yalnızca 15 sn'lik /auth/me yoklamasıyla
  // tazeleniyordu — gerçek Drakon spininde tıklama → ekran medyan 7,3 sn,
  // en çok 11,6 sn ölçüldü (06.10.2026; oyuncu hesabında 0,3-1,7 sn).
  const onAdminPanel = () => window.location.pathname.startsWith('/admin');
  useEffect(() => {
    if (!userId) return undefined;
    let stopped = false;
    let controller = null;
    const pause = (ms) => new Promise((resolve) => { window.setTimeout(resolve, ms); });
    const visible = () => new Promise((resolve) => {
      if (document.visibilityState !== 'hidden') { resolve(); return; }
      const onChange = () => {
        if (document.visibilityState === 'hidden') return;
        document.removeEventListener('visibilitychange', onChange);
        resolve();
      };
      document.addEventListener('visibilitychange', onChange);
    });

    // Bir bekleme turu; false dönerse döngü biter (uç yok ya da çıkıldı).
    const waitOnce = async () => {
      controller = new AbortController();
      const known = balanceRef.current;
      try {
        const { data } = await client.get('/auth/balance/wait', {
          params: {
            balance: known.balance, bonus_balance: known.bonus, hint: known.hint, timeout: BALANCE_WAIT_SECONDS,
          },
          signal: controller.signal,
          timeout: (BALANCE_WAIT_SECONDS + 10) * 1000,
        });
        if (stopped) return false;
        if (data?.changed) {
          // Bir sonraki bekleme yeni değerle başlamalı; render'ı beklersek
          // aynı değişiklik ikinci kez "değişti" diye döner.
          hintStampRef.current = data.wallet_hint_stamp || '';
          balanceRef.current = {
            balance: Number(data.balance), bonus: Number(data.bonus_balance), hint: hintStampRef.current,
          };
          setUser((current) => (current?.id === userId ? {
            ...current,
            balance: Number(data.balance),
            bonus_balance: Number(data.bonus_balance),
            ...(data.selected_wallet ? { selected_wallet: data.selected_wallet } : {}),
          } : current));
          // Bakiyeyle birlikte değişen çevrim alanları da tazelenir.
          syncUser();
        }
        return true;
      } catch (error) {
        if (stopped) return false;
        // Uç yoksa (eski sunucu) 15 sn'lik yoklamaya bırakılır.
        if (error?.response?.status === 404) return false;
        // Kanal canlı olduğu için iptal edildi: beklemeden döngüye dön.
        if (realtime.isLive()) return true;
        await pause(BALANCE_WAIT_RETRY_MS);
        return true;
      }
    };

    // Kanal kuruluyorsa (sayfa açılışı, yeniden bağlanma) bekleme isteği hemen
    // açılmaz: kanal REALTIME_GRACE_MS içinde canlı olursa yedek yola gerek
    // kalmaz. Eskiden açılışta kanal "connecting"ken açılan istek kanal 1 sn
    // sonra canlı olsa da 25 sn boyunca açık kalıyordu (BETRUN, 25.10.2026:
    // 3 saatte 1.354 bekleme, çoğu kanalı sağlıklı oturumlarda).
    const channelSettling = () => ['idle', 'connecting'].includes(realtime.status?.());
    const awaitChannel = (ms) => new Promise((resolve) => {
      if (!channelSettling()) { resolve(); return; }
      let off = () => {};
      const timer = window.setTimeout(() => { off(); resolve(); }, ms);
      off = realtime.onStatus((next) => {
        if (next === 'idle' || next === 'connecting') return;
        window.clearTimeout(timer);
        off();
        resolve();
      });
    });
    // Bekleme açıkken kanal canlı olursa istek iptal edilir: bakiye artık
    // kanaldan gelir, açık bağlantı boşuna 25 sn tutulmaz.
    const offStatus = realtime.onStatus((next) => {
      if (next === 'live') controller?.abort();
    });

    const loop = async () => {
      let running = true;
      while (running && !stopped) {
        // Arka plandaki sekme beklemez; öne gelince /auth/me zaten tazeler.
        await visible();
        if (stopped) return;
        // Panelde istek atılmaz; oyuncu sitesine geçilince bekleme başlar.
        // Gerçek zamanlı kanal canlıyken bakiye kanaldan gelir: bekleme isteği
        // atılmaz, kanal düşerse döngü kaldığı yerden devam eder.
        if (onAdminPanel() || realtime.isLive()) {
          await pause(BALANCE_WAIT_RETRY_MS);
        } else if (channelSettling()) {
          await awaitChannel(REALTIME_GRACE_MS);
          if (stopped) return;
          if (!realtime.isLive() && !onAdminPanel()) running = await waitOnce();
        } else {
          running = await waitOnce();
        }
      }
    };
    loop();
    return () => {
      stopped = true;
      offStatus();
      controller?.abort();
    };
  }, [userId, syncUser]);

  // Gerçek zamanlı kanal: oturum açılınca bağlanır, çıkınca kapanır.
  // Bakiye mutlak değer + as_of ile gelir; eski olay yenisinin üzerine yazılmaz.
  const walletRef = React.useRef(null);
  useEffect(() => {
    if (!userId) { realtime.stop(); walletRef.current = null; return undefined; }
    realtime.start(readToken());
    const applyWallet = (wallet, frame) => {
      const incoming = wallet ? { ...wallet, _s: frame?.s || 0 } : null;
      if (!newerWallet(walletRef.current, incoming)) return false;
      const previous = walletRef.current;
      walletRef.current = incoming;
      hintStampRef.current = incoming.wallet_hint_stamp || '';
      balanceRef.current = { balance: Number(incoming.balance), bonus: Number(incoming.bonus_balance), hint: hintStampRef.current };
      setUser((current) => {
        if (current?.id !== userId) return current;
        const next = {
          ...current,
          balance: Number(incoming.balance),
          bonus_balance: Number(incoming.bonus_balance),
          ...(incoming.selected_wallet ? { selected_wallet: incoming.selected_wallet } : {}),
        };
        const same = next.balance === current.balance && next.bonus_balance === current.bonus_balance
          && next.selected_wallet === current.selected_wallet;
        return same ? current : next;
      });
      return Boolean(previous) && (previous.balance !== incoming.balance || previous.bonus_balance !== incoming.bonus_balance
        || previous.selected_wallet !== incoming.selected_wallet || previous.wallet_hint_stamp !== incoming.wallet_hint_stamp);
    };
    // Bakiyeyle değişen çevrim alanları için /auth/me en çok 5 sn'de bir
    // (otomatik oyunda her spin bir istek olmasın).
    let syncTimer = null;
    let lastUserSync = 0;
    const syncSoon = () => {
      if (syncTimer) return;
      const wait = Math.max(0, 5000 - (Date.now() - lastUserSync));
      syncTimer = setTimeout(() => { syncTimer = null; lastUserSync = Date.now(); syncUser(); }, wait);
    };
    let accountTimer = null;
    const accountChanged = () => {
      // Bonus/finans durumu değişti: hesap (çevrim alanları) bir kez tazelenir,
      // açık ekranlar (yatırım/çekim/bonus) kendi listelerini yeniden okur.
      clearTimeout(accountTimer);
      accountTimer = setTimeout(() => {
        syncUser();
        window.dispatchEvent(new CustomEvent('bhs:account-state'));
      }, 300);
    };
    const offs = [
      realtime.on('hello', (data, frame) => {
        // Yeni bağlantıya ilk anlık görüntü; yeniden bağlanmada kaçırılan olay
        // olabilir: hesap HTTP ile bir kez senkronlanır.
        walletRef.current = null;
        applyWallet(data.wallet, frame);
        if (data.reconnect) accountChanged();
      }),
      realtime.on('wallet', (data, frame) => { if (applyWallet(data, frame)) syncSoon(); }),
      realtime.on('account', accountChanged),
    ];
    return () => { offs.forEach((off) => off()); clearTimeout(accountTimer); clearTimeout(syncTimer); };
  }, [userId, syncUser]);

  // Sekme arka plandayken bakiye tazelemenin anlamı yok, ama oyuncu hâlâ
  // sitede olabilir: oyunu ayrı sekmede açmış ya da telefonda başka
  // uygulamaya geçmiştir. Yönetim panelindeki online sayacı doğru kalsın
  // diye yalnız canlılık bildiren hafif uç çağrılır.
  useEffect(() => {
    if (!userId) return undefined;

    const beat = () => {
      if (document.visibilityState !== 'hidden') return;
      // Kanal açıkken sunucu bağlı oyuncunun last_seen_at'ini kendisi yazar.
      if (realtime.isLive()) return;
      client.post('/auth/heartbeat').catch(() => {
        // Canlılık bildirimi başarısız olursa oturuma dokunulmaz.
      });
    };

    const timer = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [userId]);

    // Yalnızca bu sekmenin başlattığı ve sessionStorage'da tuttuğu oturum
    // kurtarılır. Sunucudaki genel "aktif oturum" kapatılırsa başka sekme veya
    // cihazda henüz açılmış oyun yanlışlıkla sonlandırılabilir.
    const recoveredRef = React.useRef(false);
    useEffect(() => {
      if (!user || recoveredRef.current) return;
      recoveredRef.current = true;
      (async () => {
        let sessionId = '';
        try {
          sessionId = sessionStorage.getItem(GAME_SESSION_KEY) || '';
          if (!sessionId) return;
          const { data: res } = await client.post('/games/close', {
            session_id: sessionId,
          });
          if (res.returned > 0) {
            setUser((u) => (u ? { ...u, balance: res.balance } : u));
          }
        } catch { /* kurtarma başarısız olursa sessizce geçilir */ }
        finally {
          try {
            if (sessionStorage.getItem(GAME_SESSION_KEY) === sessionId) {
              sessionStorage.removeItem(GAME_SESSION_KEY);
            }
          } catch { /* depolama erişimi yoksa önemsenmez */ }
        }
      })();
    }, [user]);

  // `extra`: formun eklediği alanlar (bot doğrulaması jetonu `captcha_token`).
  const login = async (username, password, extra = {}) => {
    try {
      const { data } = await client.post('/auth/login', { username, password, ...extra });
      // 2FA açık hesaplarda token yerine doğrulama oturumu döner
      if (data.two_factor_required || data.registration_verification_required) {
        return {
          ok: false,
          twoFactor: {
            challenge: data.challenge,
            purpose: data.registration_verification_required ? 'register' : 'login',
            emailMasked: data.email_masked,
            phoneMasked: data.phone_masked,
            deliveryChannel: data.delivery_channel,
            deliveryTarget: data.delivery_target || data.phone_masked || data.email_masked,
            codeLength: data.code_length || 6,
          },
        };
      }
      writeToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Giriş başarısız' };
    }
  };

  const verifyTwoFactor = async (challenge, code, purpose = 'login', options = {}) => {
    try {
      const endpoint = purpose === 'register'
        ? '/auth/register/verify'
        : '/auth/login/2fa';
      const body = { challenge, code };
      // Yönetici girişi "Bu bilgisayara güven" (yalnızca giriş doğrulamasında).
      if (purpose !== 'register' && options.trustDevice) body.trust_device = true;
      const { data } = await client.post(endpoint, body);
      writeToken(data.token);
      setUser(data.user);
      if (data.referred) clearRef();
      if (purpose === 'register') markJustRegistered();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Doğrulama başarısız' };
    }
  };

  // ``alternate``: "SMS gelmedi mi?" — kod öbür SMS sağlayıcısından gider (23.10.2026).
  const resendTwoFactor = async (challenge, purpose = 'login', { alternate = false } = {}) => {
    try {
      const endpoint = {
        register: '/auth/register/resend',
        password_reset: '/auth/password/forgot-sms/resend',
      }[purpose] || '/auth/login/2fa/resend';
      await client.post(endpoint, { challenge, alternate });
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Kod gönderilemedi' };
    }
  };

  const register = async (data) => {
    try {
      // ?ref= / ?l= / ?sub= ile gelen atıf varsa kayıt gövdesine eklenir.
      const { data: res } = await client.post('/auth/register', { ...data, ...getRefPayload() });
      if (res.registration_verification_required) {
        return {
          ok: false,
          verification: {
            challenge: res.challenge,
            purpose: 'register',
            emailMasked: res.email_masked,
            phoneMasked: res.phone_masked,
            deliveryChannel: res.delivery_channel,
            deliveryTarget: res.delivery_target || res.phone_masked || res.email_masked,
            codeLength: res.code_length || 6,
          },
        };
      }
      writeToken(res.token);
      setUser(res.user);
      if (res.referred) clearRef();
      markJustRegistered();
      return { ok: true, user: res.user, referred: res.referred };
    } catch (e) {
      return { ok: false, msg: e.response?.data?.detail || 'Kayıt başarısız' };
    }
  };

  const logout = async () => {
    // Çıkış isteğinin 401'i (sunucuda zaten kapalı) "oturum sona erdi" uyarısı göstermez.
    loggingOutRef.current = true;
    try {
      await client.post('/auth/logout');
    } catch {
      // Oturum sunucuda zaten kapanmış olabilir (ör. şifre değişikliği tüm
      // oturumları düşürür → 401). Yerel çıkış yine de tamamlanır.
    } finally {
      // Bilinçli çıkış: uyarı yok. Öteki sekmeler `storage` olayıyla kapanır.
      clearRetry();
      writeToken(null);
      userRef.current = null;
      setUser(null);
      loggingOutRef.current = false;
    }
  };

  const setBalance = (bal) => setUser((u) => (u ? { ...u, balance: bal } : u));
  // Bonus Wallet modeli: bahis yalnızca seçili cüzdandan düşer. Seçimi
  // yalnızca oyuncu değiştirir; sunucu boş bonus cüzdanının seçilmesini reddeder.
  const selectWallet = async (wallet) => {
    const { data } = await client.post('/auth/wallet', { wallet });
    setUser((u) => (u ? {
      ...u, selected_wallet: data.selected_wallet, wallet_hint: null,
      balance: Number(data.cash), bonus_balance: Number(data.bonus),
    } : u));
    return data;
  };
  const setWalletBalances = ({ balance, bonus_balance: bonusBalance, selected_wallet: selected }) => {
    setUser((u) => (u ? {
      ...u,
      ...(balance != null ? { balance: Number(balance) } : {}),
      ...(bonusBalance != null ? { bonus_balance: Number(bonusBalance) } : {}),
      // Bonus iptali seçimi sunucuda ana cüzdana çevirir.
      ...(selected ? { selected_wallet: selected, wallet_hint: null } : {}),
    } : u));
    // Bakiyeyi değiştiren her ödül/iptal çevrimi de değiştirir (bonus talebi,
    // promosyon kodu, çark, iptal); çevrim alanları sunucudan tazelenir.
    syncUser();
  };

  return (
    <AuthContext.Provider value={{
      user,
      ready,
      login,
      verifyTwoFactor,
      resendTwoFactor,
      register,
      logout,
      refresh,
      syncUser,
      setBalance,
      setWalletBalances,
      balance: user?.balance || 0,
      bonusBalance: user?.bonus_balance || 0,
      selectedWallet: user?.selected_wallet === 'bonus' ? 'bonus' : 'cash',
      walletHint: user?.wallet_hint || null,
      selectWallet,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
